import _ from 'lodash'
import type {Experiment, Pointer, SerializedCompStructure} from '@wix/document-services-types'
import type {CreateExtArgs, CoreLogger} from '@wix/document-manager-core'
import {addIdToMap, getCustomId} from '../utils'
import type {DataModelExtensionAPI, RelationshipsAPI, SchemaExtensionAPI, VariantsExtensionAPI} from '../../../..'
import type {DeserializeComponentParams} from '../types'
import {DATA_TYPES, VARIANTS, VIEW_MODES} from '../../../../constants/constants'
import {createDeserializedVariantsList} from '../../../../utils/variantsUtils'
import {ReportableError} from '@wix/document-manager-utils'
import {Errors} from '../../../../errors/errors'
import {deepClone} from '@wix/wix-immutable-proxy'

/**
 * collect all variants of component and his ancestor and save it to map
 * it will help us later on to link scoped values to the variant that triggers the scoped value
 * when deserializing scoped values like (transform, transition, ...)
 */
const mapAncestorsVariantIds = (
    {extensionAPI}: CreateExtArgs,
    componentPointer: Pointer,
    oldToNewIdMap: Record<string, string>,
    isAncestorsChecked?: boolean
) => {
    const {variants} = extensionAPI as VariantsExtensionAPI

    if (!isAncestorsChecked) {
        const variantsIds = variants.collectAncestorsVariants(componentPointer)
        _.forEach(variantsIds, id => addIdToMap({id}, id, oldToNewIdMap))
    }
}

const mapMasterPageVariantIds = (
    {extensionAPI}: CreateExtArgs,
    componentPointer: Pointer,
    oldToNewIdMap: Record<string, string>
) => {
    const {variants} = extensionAPI as VariantsExtensionAPI

    const variantsIds = variants.getMasterPageVariants(componentPointer)
    _.forEach(variantsIds, id => addIdToMap({id}, id, oldToNewIdMap))
}

const validateBeforeUpdate = (
    compStructure: SerializedCompStructure,
    experimentInstance: Experiment,
    logger: CoreLogger
) => {
    if (experimentInstance.isOpen('dm_logErrorIfDuplicateHoverVariantIsAdded')) {
        _.forEach(VARIANTS.SINGLE_VARIANT_PER_COMP_TYPES, variantType => {
            const compStructureBeforeDuplicatesCleanup = deepClone(compStructure)
            const variantsByComponentId = _.groupBy(
                _.pickBy(compStructure.variants, variant => variant.type === variantType),
                'componentId'
            )
            for (const [componentId, variants] of Object.entries(variantsByComponentId)) {
                if (variants.length > 1) {
                    const duplicateIds = variants.slice(1).map(v => {
                        const variantId = _.findKey(compStructure.variants, v)
                        if (variantId) {
                            delete compStructure.variants![variantId]
                        }
                        return variantId
                    })
                    logger.captureError(
                        new ReportableError({
                            errorType: Errors.DUPLICATE_VARIANTS,
                            message: `Found ${
                                variants.length
                            } ${variantType} variants for componentId ${componentId}. Removed duplicates: ${duplicateIds.join(
                                ', '
                            )}`,
                            extras: {method: 'updateVariantsInStructure', compStructureBeforeDuplicatesCleanup}
                        })
                    )
                }
            }
        })
    }
}

/**
 * deserialize variants of component to the page, and replacing the ref from old comp id to new comp id
 */
export const updateVariantsInStructure = (
    deserializationParams: DeserializeComponentParams,
    isAncestorsChecked?: boolean
) => {
    const {createExtArgs, mappers, containerPointer, compStructure, pageId} = deserializationParams
    const {coreConfig} = createExtArgs
    const {experimentInstance} = coreConfig
    const {extensionAPI} = createExtArgs
    const {pointers} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {schemaAPI} = extensionAPI as SchemaExtensionAPI
    let {oldToNewIdMap} = mappers ?? {}
    if (!oldToNewIdMap) {
        oldToNewIdMap = {}
    }

    mapAncestorsVariantIds(createExtArgs, containerPointer!, oldToNewIdMap, isAncestorsChecked)
    mapMasterPageVariantIds(createExtArgs, containerPointer!, oldToNewIdMap)

    validateBeforeUpdate(compStructure, experimentInstance, coreConfig.logger)

    _.forEach(compStructure.variants, (variant, oldId) => {
        const variantCustomId = getCustomId(oldToNewIdMap, {id: oldId}, dataModel.generateNewId(DATA_TYPES.variants))
        const variantWithNewCompId = _.defaults({componentId: compStructure.id}, variant)

        const {values} = variant
        _.forEach(values, val => {
            if (val.id) {
                const newId = getCustomId(oldToNewIdMap, {id: val.id}, dataModel.generateNewId(DATA_TYPES.variants))!
                addIdToMap({id: val.id}, newId, oldToNewIdMap)
                val.id = newId
            }

            if (
                [VARIANTS.VARIANT_TYPES.REPEATER_PATTERN_LIST, VARIANTS.VARIANT_TYPES.VARIANTS_LIST].includes(
                    variant.type
                )
            ) {
                val.componentId = compStructure.id
            }
        })

        const possibleNonOwnedRefs = schemaAPI
            .extractReferenceFieldsInfo(DATA_TYPES.variants, variant.type, variant)
            .filter(({referencedMap}) => referencedMap === DATA_TYPES.effects)
        for (const {path, referencedMap} of possibleNonOwnedRefs) {
            const id = _.get(variant, path)
            const newId = getCustomId(oldToNewIdMap, {id}, dataModel.generateNewId(referencedMap))!
            addIdToMap({id}, newId, oldToNewIdMap)
            _.set(variantWithNewCompId, path, `#${newId}`)
        }

        const newVariantId = dataModel.addItem(variantWithNewCompId, DATA_TYPES.variants, pageId, variantCustomId).id
        if (newVariantId) {
            addIdToMap({id: oldId}, newVariantId, oldToNewIdMap)
            if (variantWithNewCompId.type === VARIANTS.VARIANT_TYPES.BREAKPOINTS_DATA) {
                compStructure.breakpointVariantsQuery = newVariantId
            }
            if (
                [VARIANTS.VARIANT_TYPES.REPEATER_PATTERN_LIST, VARIANTS.VARIANT_TYPES.VARIANTS_LIST].includes(
                    variant.type
                )
            ) {
                compStructure.variantsQuery = newVariantId
            }
            ;(function deleteThisFuncAfterFixingPresetAndHoverTemplates() {
                if (
                    [VARIANTS.VARIANT_TYPES.HOVER, VARIANTS.VARIANT_TYPES.PRESET].includes(variant.type) &&
                    experimentInstance.isOpen('dm_variantRefOwner')
                ) {
                    if (!experimentInstance.isOpen('dm_variantRefOwnerFixer')) {
                        const {relationships} = extensionAPI as RelationshipsAPI
                        const variantsListPointer = relationships.getOwningReferencesToPointer(
                            pointers.getPointer(newVariantId, DATA_TYPES.variants)
                        )[0]
                        if (variantsListPointer) {
                            compStructure.variantsQuery = variantsListPointer.id
                            return
                        }
                    }
                    compStructure.variantsQuery = createDeserializedVariantsList(
                        extensionAPI,
                        {id: compStructure.id!, type: containerPointer ? containerPointer.type : VIEW_MODES.DESKTOP},
                        deserializationParams.pageId,
                        {values: [`#${newVariantId}`]}
                    )
                }
            })()
        }
    })
}
